import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
    CustomerProfileUsersService
} from "../../../modules/customers/shared/api/service/customer-profile-users.service";
import {CustomerIbanAccount, ProfileRoleFull, RoleUser} from "../../api/entity/profile.users";
import {NotificationManager} from "../../../../../common/providers/notification";
import {TranslateManager} from "../../../../../common/translate";
import {InjectOptionDialog, OptionDialog} from "../../option-dialog";
import {CUSTOMER_USERS_TAB_ROUTE} from "../../../core/security";

const KEYS = 'CUSTOMER.DETAILS.TAB.USERS.ROLE_ACCOUNTS.';

@Component({
    selector: 'tfl-profile-user-role-accounts-details',
    template: require('./profile-user-role-accounts.details.template.html')
})
export class ProfileUserRoleAccountsDetailsComponent implements OnInit {

    readonly usersTabRoute = CUSTOMER_USERS_TAB_ROUTE;

    @Input('profile-id') profileId: number;
    @Input('profile-user-role-id') profileUserRoleId: number;
    // The user whose details are open (to offer giving them their own role)
    @Input('profile-user-id') profileUserId: number;
    @Output() accountsCountChange = new EventEmitter<number>();
    @Output() ownRoleCreated = new EventEmitter<void>();

    accounts: CustomerIbanAccount[] = [];
    profileRoleFull: ProfileRoleFull;
    loading = false;
    loadError: string = null;
    // Account being linked or unlinked: blocks the buttons until the server answers (no double clicks)
    busyAccountId: number = null;
    givingOwnRole = false;

    @InjectOptionDialog() dialog: OptionDialog;

    constructor(private _customerProfileUsersService: CustomerProfileUsersService,
                private _notificationManager: NotificationManager,
                private _translateManager: TranslateManager) {
    }

    ngOnInit(): void {
        this.loadAccounts();
    }

    get roleUsers(): RoleUser[] {
        return (this.profileRoleFull && this.profileRoleFull.users) || [];
    }

    get isShared(): boolean {
        return this.roleUsers.length > 1;
    }

    get otherUserNames(): string {
        return this.roleUsers.map(user => user.userName).join(', ');
    }

    get linkedCount(): number {
        return this.accounts.filter(account => account.associated).length;
    }

    loadAccounts(): void {
        this.loading = true;
        this.loadError = null;
        this._customerProfileUsersService.getProfileUserRoleAccounts(this.profileId, this.profileUserRoleId)
            .subscribe(
                (profileUserRole: ProfileRoleFull) => {
                    this.loading = false;
                    this.accounts = profileUserRole.accounts || [];
                    this.profileRoleFull = profileUserRole;
                    this.accountsCountChange.emit(this.linkedCount);
                },
                error => {
                    this.loading = false;
                    this.loadError = this.errorMessage(error, KEYS + 'LOAD_ERROR');
                }
            );
    }

    associateAccount(account: CustomerIbanAccount): void {
        if (this.busyAccountId !== null) {
            return;
        }
        this.busyAccountId = +account.id;
        this._customerProfileUsersService.addAccount(this.profileUserRoleId, +account.id)
            .subscribe(
                () => {
                    this.busyAccountId = null;
                    this._notificationManager.success(this._translateManager.getValue(KEYS + 'LINKED_MESSAGE'), 4000);
                    this.loadAccounts();
                },
                error => this.onActionError(error)
            );
    }

    removeAccount(account: CustomerIbanAccount): void {
        if (this.busyAccountId !== null) {
            return;
        }
        const params = {account: account.name || account.number, role: this.profileRoleFull.roleName};
        this.dialog
            .showConfirmDialog({
                title: KEYS + 'UNLINK_DIALOG.TITLE',
                descriptionLines: [{key: KEYS + 'UNLINK_DIALOG.MESSAGE', params}],
                warning: this.isShared
                    ? {key: KEYS + 'SHARED_WARNING', params: {count: this.roleUsers.length, users: this.otherUserNames}}
                    : undefined,
                confirmButtonText: KEYS + 'UNLINK',
                cancelButtonText: KEYS + 'CANCEL',
                confirmButtonClass: 'btn-danger'
            })
            .onConfirmAction(() => {
                this.busyAccountId = +account.id;
                this._customerProfileUsersService.deleteAccountFromRole(this.profileUserRoleId, +account.id)
                    .subscribe(
                        () => {
                            this.busyAccountId = null;
                            this._notificationManager.success(this._translateManager.getValue(KEYS + 'UNLINKED_MESSAGE'), 4000);
                            this.loadAccounts();
                        },
                        error => this.onActionError(error)
                    );
            });
    }

    giveOwnRole(): void {
        if (this.givingOwnRole || !this.profileUserId) {
            return;
        }
        this.dialog
            .showConfirmDialog({
                title: KEYS + 'GIVE_OWN_ROLE_DIALOG.TITLE',
                descriptionLines: [{
                    key: KEYS + 'GIVE_OWN_ROLE_DIALOG.MESSAGE',
                    params: {id: this.profileUserId, role: this.profileRoleFull.roleName}
                }],
                confirmButtonText: KEYS + 'GIVE_OWN_ROLE',
                cancelButtonText: KEYS + 'CANCEL',
                confirmButtonClass: 'btn-primary'
            })
            .onConfirmAction(() => {
                this.givingOwnRole = true;
                this._customerProfileUsersService.giveOwnRole(this.profileId, this.profileUserId)
                    .subscribe(
                        response => {
                            this.givingOwnRole = false;
                            this._notificationManager.success(response && response.message, 5000);
                            this.ownRoleCreated.emit();
                        },
                        error => {
                            this.givingOwnRole = false;
                            this._notificationManager.error(this.errorMessage(error, KEYS + 'ACTION_ERROR'));
                        }
                    );
            });
    }

    private onActionError(error: any): void {
        this.busyAccountId = null;
        this._notificationManager.error(this.errorMessage(error, KEYS + 'ACTION_ERROR'));
        // The list may have changed meanwhile (another user, another tab): show the real state
        this.loadAccounts();
    }

    private errorMessage(error: any, fallbackKey: string): string {
        return (error && error.message) || this._translateManager.getValue(fallbackKey);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/shared/profile-user/profile-user-role-accounts-details/profile-user-role-accounts.details.component.ts